import React from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { VendorLineItemObservationRecord, VendorLineItemRecord } from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import { formatInventoryDisplayName } from './inventoryFormatters';
import { DrawerBadge, DrawerField } from '../shared/LedgerDrawerPrimitives';

type InventorySeries = {
    series_id?: string;
    comparable_unit?: string;
    comparable_unit_family?: string;
    most_recent_unit_price?: number | null;
    lowest_unit_price?: number | null;
    observation_count?: number | null;
    vendorRows: Array<{
        _id?: string;
        vendor_name?: string;
        currency?: string;
        most_recent_unit_price?: number | null;
        lowest_unit_price?: number | null;
        observation_count?: number | null;
    }>;
    observations: VendorLineItemObservationRecord[];
    chartData: Array<{
        id: string;
        label: string;
        dateLabel: string;
        price: number;
        invoiceLabel: string;
        vendorName: string;
    }>;
};

type InventoryDrawerPresentation = {
    title: string;
    subtitle: string;
    amountDisplay: React.ReactNode;
    metadataContent: React.ReactNode;
};

export const buildInventoryDrawerPresentation = ({
    inventoryRecord,
    inventoryUnitSeries,
    inventoryChartCurrency,
    isObservationsLoading,
    formatDate,
    onOpenLinkedRecord,
}: {
    inventoryRecord: VendorLineItemRecord;
    inventoryUnitSeries: InventorySeries[];
    inventoryChartCurrency: string;
    isObservationsLoading: boolean;
    formatDate: (value?: string) => string;
    onOpenLinkedRecord?: (invoiceId: string, preferredSource?: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills' | null) => void;
}): InventoryDrawerPresentation => {
    const title = formatInventoryDisplayName(inventoryRecord);
    const subtitle = inventoryRecord.vendor_count
        ? `${inventoryRecord.vendor_count} vendor${inventoryRecord.vendor_count === 1 ? '' : 's'}`
        : (inventoryRecord.vendor_name || 'Vendor');

    const amountDisplay = (
        <div className="flex items-center gap-2 mt-2">
            <span className="text-3xl font-bold text-gray-900">
                {formatCurrency(inventoryRecord.most_recent_unit_price ?? inventoryRecord.unit_price, inventoryRecord.currency || 'USD')}
            </span>
            <DrawerBadge className="bg-sky-100 text-sky-800">Inventory</DrawerBadge>
        </div>
    );

    const metadataContent = (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
                <DrawerField
                    label="Vendors"
                    value={inventoryRecord.vendor_count ?? inventoryRecord.vendors?.length ?? (inventoryRecord.vendor_name ? 1 : '—')}
                />
                <DrawerField label="Currency" value={inventoryRecord.currency || '—'} />
                <DrawerField label="Lowest Price" value={formatCurrency(inventoryRecord.lowest_unit_price, inventoryRecord.currency || 'USD')} />
                <DrawerField label="Observations" value={inventoryRecord.observation_count ?? '—'} />
                <DrawerField label="First Seen" value={formatDate(inventoryRecord.first_seen_at)} />
                <DrawerField label="Last Updated" value={formatDate(inventoryRecord.most_recent_seen_at)} />
            </div>

            {inventoryUnitSeries.map((series) => (
                <div key={series.series_id || `${series.comparable_unit_family}:${series.comparable_unit}`} className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Unit Series
                        </h3>
                        <span className="text-xs text-gray-500">
                            {(series.comparable_unit || 'unknown').toUpperCase()} {series.comparable_unit_family ? `· ${series.comparable_unit_family}` : ''}
                        </span>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Recent</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {formatCurrency(series.most_recent_unit_price, inventoryChartCurrency)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Lowest</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {formatCurrency(series.lowest_unit_price, inventoryChartCurrency)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Seen</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900">{series.observation_count ?? 0}</p>
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            {isObservationsLoading ? (
                                <div className="h-[180px] flex items-center justify-center text-sm text-gray-500">
                                    Loading price trend...
                                </div>
                            ) : series.chartData.length === 0 ? (
                                <div className="h-[180px] flex items-center justify-center text-sm text-gray-500">
                                    No price observations available for this unit series.
                                </div>
                            ) : (
                                <div className="h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={series.chartData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                                            <XAxis
                                                dataKey="label"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 11, fill: '#94a3b8' }}
                                                minTickGap={20}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 11, fill: '#94a3b8' }}
                                                width={72}
                                                tickFormatter={(value) => formatCurrency(Number(value), inventoryChartCurrency)}
                                            />
                                            <Tooltip
                                                formatter={(value: number) => formatCurrency(Number(value), inventoryChartCurrency)}
                                                labelFormatter={(_label, payload) => {
                                                    const point = payload?.[0]?.payload;
                                                    if (!point) return '';
                                                    return point.dateLabel;
                                                }}
                                                contentStyle={{
                                                    borderRadius: '12px',
                                                    border: '1px solid #e5e7eb',
                                                    boxShadow: '0 10px 25px -15px rgba(15, 23, 42, 0.35)',
                                                    fontSize: '12px',
                                                }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="price"
                                                stroke="#0f766e"
                                                strokeWidth={2.5}
                                                dot={{ r: 3, fill: '#0f766e', stroke: '#ffffff', strokeWidth: 1.5 }}
                                                activeDot={{ r: 5, fill: '#ffffff', stroke: '#0f766e', strokeWidth: 2 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <div className="grid grid-cols-[minmax(0,2.2fr)_92px_80px_52px] gap-3 px-4 py-3 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <span>Vendor</span>
                                <span className="text-right whitespace-nowrap">Recent</span>
                                <span className="text-right whitespace-nowrap">Lowest</span>
                                <span className="text-right whitespace-nowrap">Seen</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {series.vendorRows.length === 0 && (
                                    <div className="px-4 py-6 text-sm text-gray-500">No vendor-level price details available.</div>
                                )}
                                {series.vendorRows.map((item) => (
                                    <div key={`vendor-price-${item._id || item.vendor_name}`} className="grid grid-cols-[minmax(0,2.2fr)_92px_80px_52px] gap-3 px-4 py-3 text-sm">
                                        <span className="truncate text-gray-900 font-medium">{item.vendor_name || '—'}</span>
                                        <span className="text-gray-700 text-right whitespace-nowrap">
                                            {formatCurrency(item.most_recent_unit_price, item.currency || inventoryRecord.currency || 'USD')}
                                        </span>
                                        <span className="text-gray-700 text-right whitespace-nowrap">
                                            {formatCurrency(item.lowest_unit_price, item.currency || inventoryRecord.currency || 'USD')}
                                        </span>
                                        <span className="text-gray-500 text-right whitespace-nowrap">{item.observation_count ?? '—'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <div className="grid grid-cols-[minmax(0,1.9fr)_96px_88px] gap-3 px-4 py-3 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <span>Vendor</span>
                                <span>Date</span>
                                <span>Price</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {isObservationsLoading && (
                                    <div className="px-4 py-6 text-sm text-gray-500">Loading invoice history...</div>
                                )}

                                {!isObservationsLoading && series.observations.length === 0 && (
                                    <div className="px-4 py-6 text-sm text-gray-500">No linked invoice observations found for this unit series.</div>
                                )}

                                {!isObservationsLoading && series.observations.map((observation) => {
                                    const canOpen = !!observation.invoice_id && !!onOpenLinkedRecord;
                                    const invoiceLabel = observation.document_number || observation.invoice_id || 'Unknown invoice';
                                    return (
                                        <div
                                            key={observation._id}
                                            className="grid w-full grid-cols-[minmax(0,1.9fr)_96px_88px] items-center gap-3 px-4 py-3 text-left text-sm"
                                        >
                                            <div className="min-w-0 space-y-1">
                                                <div className="truncate font-medium text-gray-900" title={observation.vendor_name || '—'}>
                                                    {observation.vendor_name || '—'}
                                                </div>
                                                <div>
                                                    <button
                                                        type="button"
                                                        disabled={!canOpen}
                                                        className="max-w-full truncate text-xs font-medium text-indigo-600 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-default"
                                                        onClick={() => {
                                                            if (canOpen && observation.invoice_id) {
                                                                onOpenLinkedRecord(observation.invoice_id, observation.document_source || null);
                                                            }
                                                        }}
                                                    >
                                                        Invoice {invoiceLabel}
                                                    </button>
                                                </div>
                                            </div>
                                            <span className="text-gray-900 whitespace-nowrap">
                                                {formatDate(observation.invoice_date || observation.effective_at || observation.seen_at)}
                                            </span>
                                            <span className="text-gray-900 whitespace-nowrap">
                                                {formatCurrency(observation.unit_price, observation.currency || inventoryRecord.currency || 'USD')}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return { title, subtitle, amountDisplay, metadataContent };
};
